<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
        <BaseInput
          v-model="modifiedData.name"
          color="base"
          show-error-as-tooltip
          :error="$v.name.required.$invalid ? $v.name.required.$message : ''" />
      </div>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="code" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
        <BaseInput
          v-model="modifiedData.code"
          color="base"
          show-error-as-tooltip
          :error="$v.code.required.$invalid ? $v.code.required.$message : ''" />
      </div>

      <BaseSelectDropdown
        :items="dropdownTypes"
        :selected-item="selectedItem"
        :disable="!modifiedData.isFromManager"
        label="eType"
        clearable
        show-error-as-tooltip
        :error="$v.type.required.$invalid ? $v.type.required.$message : ''"
        @update:selected="handleSelection"
        name="paymentType" />

      <BaseToggle
        :label="$t('baseObject.isActive')"
        name="isActive"
        v-model="modifiedData.isActive"
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { addOrEditPaymentType } from '@/services/api/documents';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { enumToDropdownItems } from '@/utils/enums';
import { deepCopy } from '@/utils/data';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';

import { type PaymentType, ePaymentType } from '@/types/PaymentType';
import { type SelectDropdownItem } from '@/types/Base';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    paymentType: PaymentType;
  }>(),
  {}
);

const modifiedData = ref<PaymentType>(deepCopy<PaymentType>(props.paymentType));
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.code = newData.code || '';
    validationModel.value.type = newData.eTypeString || '';

    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.paymentType);
  },
  { deep: true }
);

const dropdownTypes = ref(enumToDropdownItems(ePaymentType));

const selectedItem = ref<SelectDropdownItem | null>(null);

function handleSelection(item: SelectDropdownItem) {
  if (!modifiedData.value) return;
  selectedItem.value = item ? item : null;
  modifiedData.value.eType = item ? +item.value : null;
  modifiedData.value.eTypeString = item ? item.name : '';
}

function onDismissChanges() {
  modifiedData.value = deepCopy<PaymentType>(props.paymentType);
  emit('closeModal');
}

const toastStore = useToastStore();

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await addOrEditPaymentType(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
    emit('closeModal');
  }
}

onMounted(() => {
  if (modifiedData.value && modifiedData.value.eType !== undefined) {
    const selectedValue = dropdownTypes.value.find(
      (item) => item.value === modifiedData.value?.eType
    );

    if (selectedValue) {
      selectedItem.value = selectedValue;
    }
  }
});

const validationModel = ref({
  name: modifiedData.value.name || '',
  code: modifiedData.value.code || '',
  type: modifiedData.value.eTypeString || '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  code: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  type: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
